import React, { useReducer, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import format from "date-fns/format";
import { decode } from "jsonwebtoken";
import queryString from "query-string";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { airbrake } from "../../../utils/errors";
import casLogo from "../../../../../app/assets/images/logo_cas_full.svg";
import { GET_ADDON_QUERY } from "../../../graphql/queries";
import { CREATE_ADDON_MUTATION } from "../../../graphql/mutations";
import { transformAddOns } from "../../../modules/addons";
import { transformCheckoutError } from "../../../modules/checkout";
import FastrackPaymentForm from "../FastrackPaymentForm";
import HomebeatIcon from "../../icons/product/round/Homebeat";
import PlanPrice from "../../PlanPrice";
import {
  Container,
  AddOnSection,
  AddOnContent,
  HomeLink,
  AddOnCard,
  AddOnName,
  AddOnIcon,
  AddOnTagline,
  TrialHeading,
  TrialStatement,
  TrialDetails,
  PaymentSection,
  PaymentContent,
  FlashError
} from "./styled/add-ons";
import {
  getRecaptchaToken,
  RECAPTCHA_CREATE_ADDON
} from "../../../utils/recaptcha-v3";

const initialState = {
  mlsId: null,
  addOn: null,
  promoCode: "",
  promoInputValue: "",
  errorMsg: null,
  formikBag: null
};

function reducer(state, { type, payload }) {
  switch (type) {
    case "PARSE_URL":
    case "GET_ADDON_SUCCESS":
      return { ...state, ...payload };
    case "UPDATE_PROMO_INPUT_VALUE":
      return { ...state, promoInputValue: payload };
    case "UPDATE_PROMOCODE":
      return { ...state, promoCode: payload };
    case "CREATE_ADDON_INITIATED":
      return { ...state, formikBag: payload.formikBag };
    case "CREATE_ADDON_FAILED":
      return { ...state, errorMsg: payload };
    default:
      return state;
  }
}

function getRedirectPath(addOnKey) {
  return {
    homebeat: "homebeat"
  }[addOnKey];
}

function AddOns({ addOnKey, location: { origin, pathname } }) {
  const future = new Date();
  future.setDate(future.getDate() + window.trial_duration);
  const trialExpirationDate = format(window.trial_end_date, "MMMM DD, YYYY");
  const {
    jwt,
    promo = "",
    ...queryParams
  } = useMemo(() => queryString.parse(window.location.search), []);
  const token = jwt || sessionStorage.getItem("fastrack-jwt");
  const { user, redirect_url, exp } = decode(token) || {};

  const [state, dispatch] = useReducer(reducer, initialState);
  const { addOn, promoCode, promoInputValue, errorMsg } = state;
  const { price = 0, discountAmount = 0 } = addOn || {};
  const finalPrice = discountAmount > 0 ? price - discountAmount : price;

  // redirect if no valid jwt is detected
  if (!exp) {
    window.location.href = `${window.location.origin}/${getRedirectPath(
      addOnKey
    )}`;
    return null;
  }

  // strip jwt from url while preserving other query params
  useEffect(() => {
    const queryParamsWithoutJwt = {
      ...(!!promo && { promo }),
      ...queryParams
    };

    const newUrl = `${origin}${pathname}${
      !!promo || !!queryParams.length ? "?" : ""
    }${queryString.stringify(queryParamsWithoutJwt)}`;

    window.history.pushState({}, document.title, newUrl);
  }, []);

  // cache jwt in sessionStorage just in case the page gets refreshed
  useEffect(() => {
    if (jwt) sessionStorage.setItem("fastrack-jwt", jwt);
  }, []);

  // populate state with promo url param
  useEffect(() => {
    dispatch({
      type: "PARSE_URL",
      payload: { promoCode: promo || "", promoInputValue: promo || "" }
    });
  }, []);

  // fetch the addon matching the `addOnKey` url param
  useQuery(GET_ADDON_QUERY, {
    variables: {
      addOnKey,
      promoCode: promoCode || promo,
      mlsCode: user.mls_credential.code
    },
    onCompleted: (data) => {
      const addOnsByKey = transformAddOns(data.addOns);
      dispatch({
        type: "GET_ADDON_SUCCESS",
        payload: { addOn: addOnsByKey[addOnKey], mlsId: data.mls[0].id }
      });
    },
    onError: (error) => dispatch({ type: "GET_ADDON_FAILED", payload: error })
  });

  const [createAddon] = useMutation(CREATE_ADDON_MUTATION, {
    onCompleted: () => window.location.replace(redirect_url),
    onError: (error) => {
      if (error) {
        state.formikBag.setSubmitting(false);

        if (error.message.includes("Sku has already been taken")) {
          dispatch({
            type: "CREATE_ADDON_FAILED",
            payload: `You've already signed up for ${addOn.name}.`
          });
        } else if (error.message.includes("credit_card")) {
          const transformedErrors = error.graphQLErrors.reduce(
            (errors, graphQLError) => {
              const parsedErrors = JSON.parse(graphQLError.message).map(
                (parsedError) => transformCheckoutError(parsedError)
              );
              return errors.concat(parsedErrors);
            },
            []
          );
          dispatch({ type: "CREATE_ADDON_FAILED", payload: transformedErrors });
        } else {
          airbrake.notify({ error });
          dispatch({
            type: "CREATE_ADDON_FAILED",
            payload: "An error occurred."
          });
        }
      }
    }
  });

  // handles submission of the payment form (creates the addon subscription for the user)
  async function handleSubmit({ formValues, formikBag, braintreePayload }) {
    dispatch({ type: "CREATE_ADDON_INITIATED", payload: { formikBag } });
    const recaptchaToken = await getRecaptchaToken(RECAPTCHA_CREATE_ADDON);

    createAddon({
      variables: {
        user: {
          email: formValues.email,
          name: user.name,
          mls_id: state.mlsId,
          mls_credential_attributes: user.mls_credential
        },
        sku: {
          type: "AddOn",
          id: addOn.id
        },
        payment_method_nonce: braintreePayload.nonce,
        grecaptcha_response: recaptchaToken,
        external_user_id: user.id,
        discount_id: addOn.discountId,
        jwt
      }
    });
  }

  return (
    <Container>
      <AddOnSection>
        <AddOnContent>
          <HomeLink href="/">
            <img src={casLogo} alt="Cloud Agent Suite" />
          </HomeLink>
          {addOn && (
            <>
              <AddOnCard>
                <AddOnName>{addOn.name}</AddOnName>
                <AddOnIcon>
                  {addOn.key === "homebeat" && <HomebeatIcon />}
                </AddOnIcon>
                <AddOnTagline addOnKey={addOn.key}>
                  {addOn.tagline}
                </AddOnTagline>
                <PlanPrice
                  planKey={addOn.key}
                  planPrice={addOn.price}
                  finalPrice={finalPrice}
                  billingCycle={addOn.billingCycle}
                />
                <TrialStatement>
                  Try it free {`for ${window.trial_duration} days`}
                </TrialStatement>
              </AddOnCard>
              <TrialDetails>
                <h3>No risk, no hassle guarantee:</h3>
                <p>
                  You’ll have full access to {addOn.name}{" "}
                  {`through the entire ${window.trial_duration}-day trial period.`}
                  <strong>
                    Your trial will end at midnight on {trialExpirationDate}
                  </strong>
                  . After that your plan will be{" "}
                  <strong>${finalPrice} per year</strong>.
                </p>
              </TrialDetails>
            </>
          )}
        </AddOnContent>
      </AddOnSection>
      <PaymentSection>
        <PaymentContent>
          <TrialHeading>
            {`${window.trial_duration}`}-Day Free Trial
          </TrialHeading>
          <FastrackPaymentForm
            email={user.email}
            isFreePlan={finalPrice <= 0}
            promoInputValue={promoInputValue}
            onPromoChange={(e) =>
              dispatch({
                type: "UPDATE_PROMO_INPUT_VALUE",
                payload: e.target.value.toLowerCase()
              })
            }
            onPromoSubmit={() =>
              dispatch({ type: "UPDATE_PROMOCODE", payload: promoInputValue })
            }
            onFormSubmit={handleSubmit}
          />
          {errorMsg && (
            <FlashError data-testid="flash-error">{errorMsg}</FlashError>
          )}
        </PaymentContent>
      </PaymentSection>
    </Container>
  );
}

AddOns.proptypes = {
  addOnKey: PropTypes.string.isRequired
};

export default AddOns;
